<template>
  <button
    v-if="!props.anchor"
    ref="tooltipActivatorEl"
    class="flex grow-0 items-center"
    v-bind="attrs"
    :aria-describedby="modalOpened ? tooltipId : undefined"
    @focus="showTooltip"
    @mouseenter="showTooltip"
    @blur="hideTooltip"
    @mouseleave="hideTooltip"
    @click="modalOpened ? hideTooltip() : showTooltip()"
  >
    <slot name="activator" :opened="modalOpened"></slot>
  </button>
  <Teleport to="body">
    <Transition>
      <div
        v-if="modalOpened"
        :id="tooltipId"
        ref="tooltipEl"
        role="tooltip"
        class="rf-tooltip border-beige-10 text-body bg-white-1 shadow-tooltip text-grey-1 pointer-events-none absolute top-0 left-0 w-max items-start rounded-lg border border-solid px-5 py-4 text-left whitespace-pre-line"
        :class="contentClass"
        :style="{ zIndex }"
      >
        <slot name="tooltip"></slot>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { offset, flip, shift } from "@floating-ui/dom";
import { getCurrentInstance, nextTick, ref, useAttrs } from "vue";
import { useOverlayZIndex } from "@/utils/composables/useOverlayZIndex";
import { useFloatingUi } from "@/utils/composables/useFloatingUi";
import { TOOLTIP_DEFAULT_PLACEMENT } from "@/utils/constants/FloatingUiConstants";

const { zIndex } = useOverlayZIndex();
const { autoPosition, stopAutoPosition } = useFloatingUi();

defineOptions({ inheritAttrs: false });
const props = defineProps({
  preset: { type: String, default: null },
  position: { type: String, default: null },
  anchor: { type: HTMLElement, default: null },
  contentClass: { type: [String, Array, Object], default: null },
});

const { uid } = getCurrentInstance();
const tooltipId = `rf-tooltip-${uid}`;

const modalOpened = ref(false);
const tooltipEl = ref();
const tooltipActivatorEl = ref();
const attrs = useAttrs();

const presets = {
  default: {
    placement: props.position || TOOLTIP_DEFAULT_PLACEMENT,
    middleware: [offset(6), shift({ padding: 20 }), flip()],
  },
};

const tooltipOffset = {
  top: { x: 0, y: -10 },
  bottom: { x: 0, y: 10 },
  right: { x: 10, y: 0 },
  left: { x: -10, y: 0 },
};

const update = () =>
  nextTick(() => {
    const buttonValue = props.anchor || tooltipActivatorEl.value;
    const tooltipValue = tooltipEl.value;
    const preset = presets[props.preset] || presets.default;
    autoPosition(buttonValue, tooltipValue, preset, ({ x, y, placement }) => {
      const direction = placement.split("-")[0];
      Object.assign(tooltipValue.style, {
        transform: `translate(${x + tooltipOffset[direction].x}px, ${
          y + tooltipOffset[direction].y
        }px)`,
      });
    });
  });

const showTooltip = () => {
  modalOpened.value = true;
  update();
};
const hideTooltip = () => {
  stopAutoPosition();
  modalOpened.value = false;
};

defineExpose({ show: showTooltip, hide: hideTooltip, tooltipId });
</script>
